import {
  Component, ElementRef, OnInit,
  OnChanges, Input, Output, EventEmitter, HostBinding, trigger, transition, style, animate
} from '@angular/core';
import { FabModel } from '../../models/fab/fab.model';
import { FabStylingModel } from '../../models/fab/fab-styling.model';
import { FabButtonModel } from '../../models/fab/fab-button.model';
import { IconModel } from '../../models/icon/icon.model';
import { ButtonModel } from '../../models/button/button.model';
import { CfCoreComponent } from '../core/core.component';
import { FabTemplates } from '../../templates/fab.template';
import { TemplateService } from '../../services/template-service/template.service';
/** @hidden */
export type DirectionAnimateState = ('void' | 'right-show' | 'left-show' | 'up-show' | 'down-show');
/**
 * <p>Component is built using styling of Angular Material Fab button and has features like:
 * items (means mini fab buttons) direction, items stays opened on click and show items on component init.</p>
 * <pre>
 * <code>
 * import {{'{'}} FabButtonModel {{'}'}} from 'cedrus-fusion'
 * <</code>cf-fab<code>></code><code><</code><code>/</code>cf-fab<code>>
 * </code>
 * </pre>
 */
export class CfFabComponent extends CfCoreComponent implements OnInit, OnChanges {
/**
 * \@hiddden 
 * It is property to attach notification class when component has notifications actions.
 */
notificationClickable = '';
/**
 * <p>Example:</p>
 * <code><pre>{{'{'}}
 *  direction: "down",
 *  showButtons: true,
 *  stayOpened: true,
 *  trigger: {{'{'}} icon: {{'{'}}name: 'fa-gear'{{'}'}} {{'}'}},
 *  actionButtons: [
 *    {{'{'}} icon: {{'{'}}name:"fa-apple"{{'}'}}, display: this.shown {{'}'}},
 *    {{'{'}} icon: {{'{'}}name:"fa-star"{{'}'}}, label: "Item", labelPosition: "left" {{'}'}},
 *    {{'{'}} icon: {{'{'}}name:"fa-home"{{'}'}}, disable: this.disabled {{'}'}}
 *  ]
 * {{'}'}}</pre></code>
 * <h4>direction: string</h4>
 * Direction of opening Fab action buttons. Can be: 'up', 'right', 'down', 'left'.
 * <h4>trigger: FabButtonModel</h4>
 * FabButtonModel for the trigger fab button.
 * <h4>actionButtons: FabButtonModel[]</h4>
 * Array of buttons with type FabButtonModel for the action mini fab buttons.
 * <h4>showButtons: boolean</h4>
 * Whether to display action mini fab buttons on component init.
 * <h4>stayOpened: boolean</h4>
 * Whether to not hide action mini fab buttons on click events on them.
 */
public properties: FabModel;
/**
 * Object for all styling properties:
 * <pre>
 * {{'{'}}
 *  trigger?: FabButtonStylingModel;       // FabButtonStylingModel for trigger icon.
 *  itemsContainer?: {{'{'}}                     // Styling for fab action items container.
 *    class?: string;                      // Classes for itemsContainer section.
 *    dynamicClass?: any;                  // Dynamic classes for itemsContainer section.
 *    item?: FabButtonStylingModel;        // FabButtonStylingModel for each mini fab action item.
 *  {{'}'}};
 * {{'}'}}
 * </pre>
 */
public styling: FabStylingModel;
/**
 * Array of buttons with type FabButtonModel for the action mini fab buttons.
 */
actionButtons: FabButtonModel[];
/**
 * Direction of opening Fab action buttons. Can be: up, right, down, left.
 * @param {?} value
 * @return {?}
 */
set direction(value: string) { this.properties.direction = value; }
/**
 * Trigger button icon name.
 * @param {?} value
 * @return {?}
 */
set iconName(value: string) {
    if (this.properties.trigger == null) {
      this.properties.trigger = new FabButtonModel({button: new ButtonModel({iconProperty: new IconModel()})});
    }
    this.properties.trigger.button.iconProperty.name = value;
    this.properties.trigger.button.iconProperty.type = value.startsWith('fa-') ? 'fa' : 'mi';
  }
/**
 * Trigger button icon size.
 * @param {?} value
 * @return {?}
 */
set iconSize(value: string) {
    if (this.properties.trigger == null) {
      this.properties.trigger = new FabButtonModel({button: new ButtonModel({iconProperty: new IconModel()})});
    }
    this.properties.trigger.button.iconProperty.size = value;
  }
/**
 * Trigger button tooltip text.
 * @param {?} value
 * @return {?}
 */
set label(value: string) {
    if (this.properties.trigger == null) {
      this.properties.trigger = new FabButtonModel({button: new ButtonModel({iconProperty: new IconModel()})});
    }
    this.properties.trigger.button.label = value;
  }
/**
 * Trigger button tooltip text position.
 * @param {?} value
 * @return {?}
 */
set tooltipPosition(value: string) {
    if (this.properties.trigger == null) {
      this.properties.trigger = new FabButtonModel({button: new ButtonModel({iconProperty: new IconModel()})});
    }
    this.properties.trigger.tooltipPosition = value;
  }
/**
 * Whether to display action mini fab buttons on component init.
 * @param {?} value
 * @return {?}
 */
set showButtons(value: boolean) { this.properties.showButtons = value; }
/**
 * Whether to not hide action mini fab buttons on click events on them.
 * @param {?} value
 * @return {?}
 */
set stayOpened(value: boolean) { this.properties.stayOpened = value; }
/**
 * @hidden
 */
public animateState: DirectionAnimateState;
/**
 * <p>Event to call when Fab trigger button was clicked</p>
 */
onClick: EventEmitter<any> = new EventEmitter<any>();
/**
 * <p>Event to call when Fab action button was clicked</p>
 */
onItemClick: EventEmitter<any> = new EventEmitter<any>();
/**
 * @hidden
 * @param {?} elementRef
 * @param {?} templateService
 */
public constructor(
public elementRef: ElementRef, /**@hidden */ templateService: TemplateService) {
    super(elementRef, templateService);
    this.properties = new FabModel();
    this.styling = new FabStylingModel();
  }
/**
 * @hidden
 * @return {?}
 */
ngOnInit(): void {
    this.getMyTemplate('fab', FabTemplates).then(() => {
      if (this.notificationAction.observers.length > 0) { this.notificationClickable = 'notification-clickable'; }
      this.mergeProperties(this.activeTemplate.property, this.properties, 'FAB').then( sampler => {
        if (this.actionButtons != null) { this.properties.actionButtons = this.actionButtons; }
        if (this.properties.showButtons) { this.setAnimateState(); }
        // console.log('FAB', this);
      });
    });
  }
/**
 * @hidden 
 * Update the properties object with the changes on the inputs to keep the component dynamic
 * @param {?} changes
 * @return {?}
 */
ngOnChanges(changes: any) {
    if (this.properties != null) {
      if (changes.showButtons != null) {
        this.setAnimateState();
      }
    }
  }
/**
 * @hidden 
 * Function to set open/close animation direction for action buttons
 * @return {?}
 */
setAnimateState() {
    if (this.properties) {
      switch (this.properties.direction) {
        case 'right': this.animateState = 'right-show'; break;
        case 'left': this.animateState = 'left-show'; break;
        case 'down': this.animateState = 'down-show'; break;
        case 'up': this.animateState = 'up-show'; break;
        default: this.animateState = 'left-show';
      }
    }
  }
/**
 * @hidden 
 * Trigger button action with emitting event with trigger button object
 * @param {?} triggerpa
 * @return {?}
 */
triggerAction(triggerpa) {
    this.properties.showButtons = !this.properties.showButtons;
    if (!this.properties.showButtons) {
      this.animateState = 'void';
    } else {
      this.setAnimateState();
    }
    if (triggerpa) { this.onClick.emit(triggerpa); }
  }
/**
 * @hidden 
 * Action button action with emitting event with trigger button object and it index
 * @param {?} index  action button index
 * @param {?} button  action button object
 * @return {?}
 */
itemAction(index, button) {
    if (this.properties) {
      this.onItemClick.emit({ cfIndex: index, cfItem: button });
      if (!this.properties.stayOpened) { this.triggerAction(null); }
    }
  }
static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
  selector: 'cf-fab',
  template: "<div *ngIf=\"_inited && properties?.display\" class=\"{{activeTemplate.style?.container?.class}} {{styling?.container?.class}} {{getDynamicClasses(activeTemplate.style?.container?.dynamicClass)}} {{getDynamicClasses(styling?.container?.dynamicClass)}}\" [ngClass]=\"'cf-fab-container'\"> <cf-button _buttonScheme=\"mat-fab\" [properties]=\"properties?.trigger?.button\" md-tooltip=\"{{properties?.trigger?.button?.label}}\" tooltip-position=\"{{properties?.trigger?.tooltipPosition ? properties?.trigger?.tooltipPosition : 'above'}}\" [styling]=\"styling?.trigger?.button\" (onClick)=\"triggerAction($event)\"></cf-button> <div class=\"direction-{{properties?.direction}} {{activeTemplate.style?.itemsContainer?.class}} {{styling?.itemsContainer?.class}} {{getDynamicClasses(activeTemplate.style?.itemsContainer?.dynamicClass)}} {{getDynamicClasses(styling?.itemsContainer?.dynamicClass)}}\" [ngClass]=\"'cf-fab-actions'\"> <div *ngFor=\"let btn of properties?.actionButtons, let i = index\" class=\"cf-fab-action-item\"> <cf-button _buttonScheme=\"mat-mini-fab\" *ngIf=\"properties?.showButtons\" class=\"cf-fab-action-button\" md-tooltip=\"{{btn?.button?.label}}\" tooltip-position=\"{{btn?.tooltipPosition ? btn?.tooltipPosition : 'above'}}\" [@buttonAnimation]=\"animateState\" [properties]=\"btn.button\" [styling]=\"styling?.itemsContainer?.item\" (onClick)=\"itemAction(i, btn)\"></cf-button> </div> </div> </div>",
  styles: [":host { position: relative; z-index: 100; display: inline-flex; width: 56px; height: 56px; } :host button.cf-fab-trigger { position: relative; z-index: 1010; margin: 8px; } :host div.cf-fab-actions { position: absolute; z-index: 1000; display: inline-flex; } :host div.cf-fab-actions div.cf-fab-action-item { display: flex; flex-direction: column; align-items: center; justify-content: center; } :host div.cf-fab-actions.direction-right { top: 50%; left: 50%; padding-left: 36px; height: 0px; align-items: center; justify-content: flex-start; flex-direction: row; } :host div.cf-fab-actions.direction-left { top: 50%; right: 50%; padding-right: 36px; height: 0px; align-items: center; justify-content: flex-end; flex-direction: row-reverse; } :host div.cf-fab-actions.direction-down { top: 50%; left: 50%; padding-top: 36px; width: 0px; justify-content: flex-start; flex-direction: column; } :host div.cf-fab-actions.direction-up { bottom: 50%; left: 50%; padding-bottom: 36px; width: 0px; justify-content: flex-end; flex-direction: column-reverse; } :host /deep/ cf-button > div > button.mat-mini-fab { min-width: 0px; } :host /deep/ cf-button > div > button > span > label { display: none !important; } :host /deep/ [hidden] { display: none !important; } "],
  animations: [
    trigger('buttonAnimation',
      [
        transition('void => right-show',
          [style({ transform: 'translateX(-100%) scale(0.5)', opacity: 0 }), animate('400ms ease-out',
            style({ transform: 'translateX(0) scale(1)', opacity: 1 }))]),
        transition('right-show => void',
          [style({ transform: 'translateX(0) scale(1)', 'opacity': 1 }), animate('400ms ease-in',
            style({ transform: 'translateX(-100%) scale(0.5)', opacity: 0 }))]),
        transition('void => left-show',
          [style({ transform: 'translateX(100%) scale(0.5)', opacity: 0 }),
          animate('400ms ease-out',
            style({ transform: 'translateX(0) scale(1)', opacity: 1 }))]),
        transition('left-show => void',
          [style({ transform: 'translateX(0) scale(1)', 'opacity': 1 }),
          animate('400ms ease-in',
            style({ transform: 'translateX(100%) scale(0.5)', opacity: 0 }))]),
        transition('void => down-show',
          [style({ transform: 'translateY(-100%) scale(0.5)', opacity: 0 }),
          animate('400ms ease-out',
            style({ transform: 'translateY(0) scale(1)', opacity: 1 }))]),
        transition('down-show => void',
          [style({ transform: 'translateY(0) scale(1)', 'opacity': 1 }),
          animate('400ms ease-in',
            style({ transform: 'translateY(-100%) scale(0.5)', opacity: 0 }))]),
        transition('void => up-show',
          [style({ transform: 'translateY(100%) scale(0.5)', opacity: 0 }),
          animate('400ms ease-out',
            style({ transform: 'translateY(0) scale(1)', opacity: 1 }))]),
        transition('up-show => void',
          [style({ transform: 'translateY(0) scale(1)', 'opacity': 1 }),
          animate('400ms ease-in',
            style({ transform: 'translateY(100%) scale(0.5)', opacity: 0 }))])
      ]
    )
  ]
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: ElementRef, },
{type: TemplateService, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'notificationClickable': [{ type: HostBinding, args: ['class', ] },],
'properties': [{ type: Input },],
'styling': [{ type: Input },],
'actionButtons': [{ type: Input },],
'direction': [{ type: Input, args: ['direction', ] },],
'iconName': [{ type: Input, args: ['iconName', ] },],
'iconSize': [{ type: Input, args: ['iconSize', ] },],
'label': [{ type: Input, args: ['label', ] },],
'tooltipPosition': [{ type: Input, args: ['tooltipPosition', ] },],
'showButtons': [{ type: Input, args: ['showButtons', ] },],
'stayOpened': [{ type: Input, args: ['stayOpened', ] },],
'onClick': [{ type: Output },],
'onItemClick': [{ type: Output },],
};
}

function CfFabComponent_tsickle_Closure_declarations() {
/** @type {?} */
CfFabComponent.decorators;
/**
 * @nocollapse
 * @type {?}
 */
CfFabComponent.ctorParameters;
/** @type {?} */
CfFabComponent.propDecorators;
/**
 * \@hiddden 
 * It is property to attach notification class when component has notifications actions.
 * @type {?}
 */
CfFabComponent.prototype.notificationClickable;
/**
 * <p>Example:</p>
 * <code><pre>{{'{'}}
 *  direction: "down",
 *  showButtons: true,
 *  stayOpened: true,
 *  trigger: {{'{'}} icon: {{'{'}}name: 'fa-gear'{{'}'}} {{'}'}},
 *  actionButtons: [
 *    {{'{'}} icon: {{'{'}}name:"fa-apple"{{'}'}}, display: this.shown {{'}'}},
 *    {{'{'}} icon: {{'{'}}name:"fa-star"{{'}'}}, label: "Item", labelPosition: "left" {{'}'}},
 *    {{'{'}} icon: {{'{'}}name:"fa-home"{{'}'}}, disable: this.disabled {{'}'}}
 *  ]
 * {{'}'}}</pre></code>
 * <h4>direction: string</h4>
 * Direction of opening Fab action buttons. Can be: 'up', 'right', 'down', 'left'.
 * <h4>trigger: FabButtonModel</h4>
 * FabButtonModel for the trigger fab button.
 * <h4>actionButtons: FabButtonModel[]</h4>
 * Array of buttons with type FabButtonModel for the action mini fab buttons.
 * <h4>showButtons: boolean</h4>
 * Whether to display action mini fab buttons on component init.
 * <h4>stayOpened: boolean</h4>
 * Whether to not hide action mini fab buttons on click events on them.
 * @type {?}
 */
CfFabComponent.prototype.properties;
/**
 * Object for all styling properties:
 * <pre>
 * {{'{'}}
 *  trigger?: FabButtonStylingModel;       // FabButtonStylingModel for trigger icon.
 *  itemsContainer?: {{'{'}}                     // Styling for fab action items container.
 *    class?: string;                      // Classes for itemsContainer section.
 *    dynamicClass?: any;                  // Dynamic classes for itemsContainer section.
 *    item?: FabButtonStylingModel;        // FabButtonStylingModel for each mini fab action item.
 *  {{'}'}};
 * {{'}'}}
 * </pre>
 * @type {?}
 */
CfFabComponent.prototype.styling;
/**
 * Array of buttons with type FabButtonModel for the action mini fab buttons.
 * @type {?}
 */
CfFabComponent.prototype.actionButtons;
/**
 * @hidden
 * @type {?}
 */
CfFabComponent.prototype.animateState;
/**
 * <p>Event to call when Fab trigger button was clicked</p>
 * @type {?}
 */
CfFabComponent.prototype.onClick;
/**
 * <p>Event to call when Fab action button was clicked</p>
 * @type {?}
 */
CfFabComponent.prototype.onItemClick;
/**
 * @hidden
 * @type {?}
 */
CfFabComponent.prototype.elementRef;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
